import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetAssignedDriverQuery,
  useLazyVehicleRecorderMediaTokenQuery,
  useOrganizationVehiclesEventsQuery,
  useSingleOrganizationVehicleQuery,
  useVehicleRecorderStatusQuery,
} from "../../api/network/adminApiServices";
import { useGetDeviceListingQuery } from "../../api/network/camerasAdminVMApiService";
import {
  setModalsData,
  setVehicleLiveCamerasData,
  TModalsState,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import {
  ASSET_MAP_STATUS,
  TLegacyVehicleRecorderMediaToken,
} from "../../api/types/Vehicle";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import svgIcons from "../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import { useACL } from "../../hooks/acl";
import { routeUrls } from "../../navigation/routeUrls";
import { TMapType } from "../../types/map";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import { convertKmphTo } from "../../utils/distance";
import { roundOffLatLong } from "../../utils/map";
import { useLoggedInUserData } from "../../utils/user";
import {
  consistentDisplayAssetItem,
  getVehicleEventStatusDisplayName,
} from "../../utils/vehicle";
import {
  ACCOUNT_RECORDER_TYPE_VALUES,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import ModalMaintenanceFaultCodes from "../admin-details/maintenance/components/modalMaintenanceFaultCodes";
import {
  getVehicleFieldValueFromUUID,
  mapVehicleDisplayIds,
  mapVehicleDisplayTitle,
} from "./common";
import { mapVehicleIsActive, mapVehicleOrientation } from "./map";
import ModalSendLocation from "./modalSendLocation";
import ModalSendText from "./modalSendText";
import ModalWeatherForecast from "./modalWeatherForecast";
import { TAssetLocationDataPoint, TDataPoint } from "./type";
import { localStorageKeys, useLocalStorage } from "../../utils/localStorageItems";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../api/types/User";
// import VehicleLiveVideo from "../../components/vehicleLiveVideo/vehicleLiveVideo";

type ModalVehicleDetailsProps = {
  eventTimestamp: number;
  handleMapTypeChange?: (
    mapType: TMapType,
    latLng?: google.maps.LatLng
  ) => void;
  vehicleData?: TDataPoint | TAssetLocationDataPoint;
  vehicleId?: any;
  onDetailModalClose: () => void;
};

type TEventSpec = {
  name: string;
  value: ReactNode | string | number;
  sub?: string | null;
};

const ModalVehicleDetails: FC<ModalVehicleDetailsProps> = ({
  eventTimestamp,
  handleMapTypeChange,
  vehicleData,
  vehicleId,
  onDetailModalClose,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.details",
  });
  const { t: tCameras } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
  (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
  loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const liveviewDurationSeconds =
  loggedInUser?.account?.cameraliveviewduration ?? 30;

  const { getUserACLAccessLevel } = useACL();
  // video allowed at account level
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");

  // fetch single vehicle details
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const { data: dataSingleVehicle, isFetching: isFetchingSingleVehicle } =
    useSingleOrganizationVehicleQuery(
      {
        account_id: thisUserOrganizationId,
        device_id:
          vehicleId?.length > 0
            ? getVehicleFieldValueFromUUID(vehicleId, "deviceid")
            : "",
      },
      { skip: !vehicleId, refetchOnMountOrArgChange: true } // || !!vehicleData - removed from skip condition as we should always fetch vehicle data from API for extra data, like is_recording_supported
    );

  // get start and end of day in UTC
  const [startEndinUTC, setStartEndinUTC] = useState<{
    startOfDayInUTC: Date;
    endOfDayInUTC: Date;
  }>({ startOfDayInUTC: new Date(0), endOfDayInUTC: new Date(0) });
  useEffect(() => {
    if (!dataSingleVehicle || isFetchingSingleVehicle) return;
    const refDateStringInTimezone = formattedDateTimeBasedOnTimezone(
      dbTimeToJsTime(
        !!eventTimestamp
          ? // check if eventTimestamp is in milliseconds or seconds
            Math.abs(Date.now() - eventTimestamp) <
            Math.abs(Date.now() - eventTimestamp * 1000)
            ? jsTimeToDBTime(eventTimestamp)
            : eventTimestamp
          : dataSingleVehicle?.lasteventtimestamp || 0
      ),
      accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
      "yyyy-MM-dd"
    );
    const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(
      refDateStringInTimezone,
      accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE
    );
    setStartEndinUTC({ startOfDayInUTC, endOfDayInUTC });
    // console.log('eventTimestamp', jsTimeToDBTime(eventTimestamp));
    // console.log('dataSingleVehicle?.lasteventtimestamp', dataSingleVehicle?.lasteventtimestamp);
    // console.log('refDateStringInTimezone', refDateStringInTimezone);
    // console.log('startEndinUTC', startEndinUTC);
  }, [
    dataSingleVehicle,
    isFetchingSingleVehicle,
    eventTimestamp,
    accountData?.timezone,
  ]);

  // fetch vehicle events data
  const { data: dataVehicleEvents, isFetching: isFetchingVehicleEvents } =
    useOrganizationVehiclesEventsQuery(
      {
        account_id: thisUserOrganizationId?.toString() || "",
        device_id:
          vehicleId?.length > 0
            ? getVehicleFieldValueFromUUID(vehicleId, "deviceid")
            : "",
        timestamp:
          eventTimestamp || dataSingleVehicle?.lasteventtimestamp || undefined,
        start_day_timestamp: jsTimeToDBTime(
          startEndinUTC.startOfDayInUTC.getTime()
        ),
        end_day_timestamp: jsTimeToDBTime(
          startEndinUTC.endOfDayInUTC.getTime()
        ),
      },
      {
        skip:
          !vehicleId ||
          startEndinUTC.startOfDayInUTC.getTime() === 0 ||
          startEndinUTC.endOfDayInUTC.getTime() === 0 ||
          isFetchingSingleVehicle,
      }
    );

  // fetch vehicle recorder status
  const {
    data: dataVehicleRecorderStatus,
    isFetching: isFetchingVehicleRecorderStatus,
    refetch: refetchVehicleRecorderStatus,
  } = useVehicleRecorderStatusQuery(
    {
      account_id: thisUserOrganizationId ?? "",
      device_id:
        vehicleId?.length > 0
          ? getVehicleFieldValueFromUUID(vehicleId, "deviceid")
          : "",
    },
    {
      skip:
        !vehicleId ||
        isFetchingSingleVehicle ||
        !dataSingleVehicle ||
        dataSingleVehicle?.is_recording_supported !== true,
      refetchOnMountOrArgChange: true, // caching removed to ensure status always shows correct status
    }
  );

  // lazy load vehicle recorder media token
  const [
    vehicleRecorderMediaTokenApiTrigger,
    { isLoading: isLoadingVehicleRecorderMediaToken },
  ] = useLazyVehicleRecorderMediaTokenQuery();

  // fetch single driver details
  const {
    data: dataAssignedDriver,
    isLoading: isLoadingAssignedDriver,
    isError: isErrorAssignedDriver,
  } = useGetAssignedDriverQuery(
    {
      account_id: thisUserOrganizationId ?? "",
      device_id:
        vehicleId?.length > 0
          ? getVehicleFieldValueFromUUID(vehicleId, "deviceid")
          : "",
    },
    { skip: !vehicleId }
  );

  const vehicleDataToUse: TDataPoint =
    (vehicleData as TDataPoint) || dataSingleVehicle;
  const [eventData, setEventData] = useState(vehicleDataToUse?.eventData);
  useEffect(() => {
    if (isFetchingVehicleEvents) return;
    setEventData(dataVehicleEvents?.[0] ?? vehicleDataToUse?.eventData);
  }, [dataVehicleEvents, vehicleDataToUse, isFetchingVehicleEvents]);

  const [listData, setListData] = useState<TEventSpec[]>([]);

  const orgDistanceUnit = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || SPEED_UNITS_OPTIONS[0].unit;

  const thisOrgCameraRecorderType = useLoggedInUserData("cameraRecorderType");

  const { data: dataDeviceListing, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: dataSingleVehicle?.cameraid, // deviceEventFromState.cameraid,
        model: "",
        status: "",
        offset: 0,
        limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      },
      {
        skip:
          thisOrgCameraRecorderType !==
            ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
          !dataSingleVehicle?.cameraid,
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  useEffect(() => {
    if (vehicleDataToUse && !isFetchingVehicleEvents) {
      const assetStatusIsActive = !!(
        vehicleDataToUse as any as TAssetLocationDataPoint
      )?.asset_map_status
        ? // use asset_map_status if available
          (vehicleDataToUse as any as TAssetLocationDataPoint)
            ?.asset_map_status !== ASSET_MAP_STATUS.NOT_REPORTING
        : // else use initial logic
          mapVehicleOrientation(vehicleDataToUse) > 0 ||
          mapVehicleIsActive(vehicleDataToUse);

      let lastIgnitionOnTime =
        vehicleDataToUse.lastengineontime ||
        dataSingleVehicle?.lastignitionontime ||
        null;
      let lastIgnitionOffTime =
        vehicleDataToUse.lastengineofftime ||
        dataSingleVehicle?.lastignitionofftime ||
        null;

      setListData([
        {
          name: "Current Ignition Status",
          value:
            eventData?.ignition_state_descr?.toUpperCase() || "Not Available",
        },
        {
          name: "Date/Time",
          value: eventData?.timestamp
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${eventData?.timestamp}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "-",
        },
        // {
        //   name: "GPS",
        //   value: eventData?.gpsage || "Not Available"
        // },
        {
          name: "Speed",
          value: !!eventData?.speedkph
            ? Math.round(convertKmphTo(speedUnit, eventData?.speedkph)) // rounding off per client requirement
            : // ?.toFixed(2)
              // .replace(".00", "")
              APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS[
                  `${eventData?.ignitionstate}` as keyof typeof APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS
                ] === "ON"
              ? "0.0"
              : "Not Available",
          sub:
            !!eventData?.speedkph ||
            APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS[
              `${eventData?.ignitionstate}` as keyof typeof APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS
            ] === "ON"
              ? `in ${
                  SPEED_UNITS_OPTIONS.find(
                    (option) => option.unit === speedUnit
                  )?.label || SPEED_UNITS_OPTIONS[0].label
                }`
              : null,
        },
        {
          name: "Speed Limit",
          value: !!eventData?.speedlimitkph
            ? Math.round(convertKmphTo(speedUnit, eventData?.speedlimitkph)) // rounding off per client requirement
            : // ?.toFixed(2)
              // .replace(".00", "")
              "Not Available",
          sub: !!eventData?.speedlimitkph
            ? `in ${
                SPEED_UNITS_OPTIONS.find((option) => option.unit === speedUnit)
                  ?.label || SPEED_UNITS_OPTIONS[0].label
              }`
            : null,
        },
        {
          name: "Fault Codes",
          value: !!dataSingleVehicle?.fault_code_info ? (
            <span
              className="text-blue-500 underline cursor-pointer"
              onClick={() => {
                // Handle link click, show fault codes modal
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showVehicleDetails: true,
                    showModalMaintenanceFaultCodes: true,
                  })
                );
              }}
            >
              {Object.keys(dataSingleVehicle?.fault_code_info).join("; ")}
            </span>
          ) : (
            "No DTC's available"
          ),
        },
        {
          name: "Status",
          value: (
            <span className="font-bold uppercase">
              {!!eventData?.status?.statuscode
                ? getVehicleEventStatusDisplayName(
                    eventData?.status as Partial<VehicleMapDataPoint>
                  )
                : "Not Available"}
              {/* {JSON.stringify(eventData)} */}
              {/* {assetStatusIsActive ? (
              <span className="text-field-success">{tMain("active")}</span>
            ) : (
              <span className="text-field-error-dark">
                {tMain("inactive")}
              </span>
            )} */}
            </span>
          ),
        },
        {
          name: "Latitude/Longitude",
          value:
            eventData?.latitude === 0 && eventData?.longitude === 0
              ? "0, 0"
              : eventData?.latitude
                ? `${roundOffLatLong(eventData?.latitude)}, ${roundOffLatLong(eventData?.longitude)}`
                : "Not Available",
        },
        {
          name: "Address",
          value: eventData?.address || "Not Available",
        },
        ...(showDriver
          ? [
              {
                name: "Driver",
                value:
                  // error in response implies that driver is not assigned
                  !vehicleId ||
                  !!isErrorAssignedDriver ||
                  !dataAssignedDriver?.driverid
                    ? "Not Available"
                    : [
                        // dataAssignedDriver?.displayname,
                        dataAssignedDriver?.description,
                        dataAssignedDriver?.driverid,
                      ]
                        .filter((item) => !!item)
                        .join(" | ") || "Not Available",
              },
            ]
          : []),
        {
          name: "Driver Phone",
          value:
            // error in response implies that driver is not assigned
            !vehicleId ||
            !!isErrorAssignedDriver ||
            !dataAssignedDriver?.driverid
              ? "Not Available"
              : dataAssignedDriver?.contactphone || "Not Available",
        },
        {
          name: "First Ignition",
          value: eventData?.first_ignition
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${eventData?.first_ignition}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "Not Available",
          // !!eventData?.ignitionstate
          //   ? APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS[`${eventData?.ignitionstate}` as keyof typeof APP_CONFIG.MAPS.VEHICLE_IGNITION_STATE_LABELS]
          //   : "Not Available"
        },
        {
          name: "Groups",
          value:
            vehicleDataToUse?.groups
              ?.map((group) => group.groupid)
              .join(", ") || "Not Available",
        },
        {
          name: "Last Valid Event",
          value: vehicleDataToUse?.lasteventtimestamp
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(
                  parseInt(`${vehicleDataToUse?.lasteventtimestamp}`)
                ),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "-",
        },
        {
          name: "Has Impact",
          value: "Not Available", // TODO: need to discuss
        },
        {
          name: "Has FleetTag",
          value: eventData?.rfidtag || "Not Available",
        },
        {
          name: "Last Ignition On",
          value: lastIgnitionOnTime
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${lastIgnitionOnTime}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "Not Available",
        },
        {
          name: "Last Ignition Off",
          value: lastIgnitionOffTime
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${lastIgnitionOffTime}`)),
                accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                "LLL dd, yyyy HH:mm:ss"
              ) + ` (${accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE})`
            : "Not Available",
        },
      ]);
    }
  }, [
    vehicleId,
    vehicleDataToUse,
    dataAssignedDriver,
    isErrorAssignedDriver,
    isFetchingVehicleEvents,
    dataVehicleEvents,
    eventData,
  ]);

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showVehicleDetails: false }));
    onDetailModalClose();
  };

  const [drawerOpen, setDrawerOpen] = useState(true);
  useEffect(() => {
    if (!!vehicleData) {
      setDrawerOpen(true);
    }
  }, [vehicleData]);

  const DrawerHandle = useCallback(
    () => (
      <div
        className="absolute bg-white w-8 h-16 shadow-[-5px_0_5px_rgba(0,0,0,0.1)] flex items-center justify-center rounded-tl-lg rounded-bl-lg top-2/4 -left-8 transform -translate-y-8 cursor-pointer"
        onClick={() => setDrawerOpen(!drawerOpen)}
      >
        <img
          src={svgIcons.ChevronRight}
          alt="chevron-right"
          className={`w-6 h-6 ${drawerOpen ? "" : "transform rotate-180"}`}
        />
      </div>
    ),
    [drawerOpen]
  );

  // media token is not in use for now
  const [mediaToken, setMediaToken] =
    useState<TLegacyVehicleRecorderMediaToken | null>(null);

  // reset media token on change of vehicleId
  useEffect(() => {
    setMediaToken(null);
  }, [vehicleId]);
  // operations after media token is retrieved
  const onMediaTokenRetrieved = useCallback(() => {
    // media token is not in use for now // (mToken: TLegacyVehicleRecorderMediaToken) => {
    console.log("OPEN MODAL");
    dispatch(
      setVehicleLiveCamerasData({
        authToken: dataVehicleRecorderStatus?.access_token || "",
        imei: dataVehicleRecorderStatus?.imei || "",
        cameras:
          dataVehicleRecorderStatus?.cameras.map((camera) => camera.cameraId) ||
          [],
        // cameras: [1, 2, 51, 52],
        organizationId:
          dataVehicleRecorderStatus?.organization?.toString() || "",
      })
    );
    dispatch(
      setModalsData({
        ...modalsState,
        showVehicleLiveCameras: true,
      })
    );
  }, [dataVehicleRecorderStatus, dispatch, modalsState]);

  // visionmax - wakeup camera success callback
  const onCameraWakeupSuccess = useCallback(
    (serialNumber: string) => {
      // refetch camera device listing
      // dispatch({
      //   type: `${reducerPath}/invalidateTags`,
      //   payload: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
      // });
      refetchVehicleRecorderStatus();

      setTimeout(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
        toast.info(
          "Allowing a few seconds for the camera to wake up and connect with servers..."
        );
        // if (!!serialNumber) {
        //   // the API uses `serial_number` instead of `cid`
        //   dispatch(
        //     setModalsData({
        //       ...modalsState,
        //       showModalVMCameraVoicecallSN: serialNumber,
        //     })
        //   );
        // }
        setTimeout(() => {
          dispatch(
            setModalsData({ ...modalsState, showPrimaryOverlay: false })
          );
          // Initiate Camera live view for currently selected camera
          if (!!serialNumber) {
            dispatch(
              setModalsData({
                ...modalsState,
                showModalWakeupCamera: null,
                showModalVMCameraLiveviewContinueSN: null,
                showModalVMCameraLiveviewSN: serialNumber,
              })
            );
            // Show Info. of live view (That It'll be available for only 10 minutes)
            setTimeout(() => {
              toast.info(
                tCameras("live_feed_info", {
                  seconds: liveviewDurationSeconds,
                })
              );
            }, 3 * 1000);
          }
        }, 10 * 1000); // allowing 10 seconds
      }, 2500); // extra 2.5 seconds
    },
    [modalsState, liveviewDurationSeconds]
  );

  // handle vehicle live camera click
  const handleVehicleLiveCameraClick = useCallback(() => {
    if (dataVehicleRecorderStatus?.recorder_status === "Unknown") {
      toast.error(t("recorder_status.unknown"));
    } else if (
      dataVehicleRecorderStatus?.recorder_status === "standby" ||
      dataVehicleRecorderStatus?.recorder_status === "offline"
    ) {
      // open modal
      if (
        thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX &&
        !!dataSingleVehicle?.cameraid &&
        !!dataDeviceListing?.data?.[0]?.cid
      ) {
        // visionmax
        dispatch(
          setModalsData({
            ...modalsState,
            showModalWakeupCamera: {
              cid: dataDeviceListing?.data?.[0]?.cid || "",
              serial_number: dataSingleVehicle.cameraid,
              onSuccessCallback: onCameraWakeupSuccess,
            },
          })
        );
      } else {
        // surfsight
        toast.error(t("recorder_status.standby"));
      }
    } else if (dataVehicleRecorderStatus?.recorder_status === "online") {
      // open modal
      if (
        thisOrgCameraRecorderType === ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX
      ) {
        // visionmax
        if (!!dataSingleVehicle?.cameraid) {
          dispatch(
            setModalsData({
              ...modalsState,
              showModalVMCameraLiveviewSN: dataSingleVehicle.cameraid,
            })
          );
        } else {
          toast.error(t("camera_serial_number_not_found"));
        }
      } else {
        // surfsight
        onMediaTokenRetrieved();
      }

      // following code is commented as media token is not in use for now
      // ----------------------------------------------------------------
      // check and retrieve media token if not already available
      // if (mediaToken) {
      //   onMediaTokenRetrieved(mediaToken);
      // } else {
      //   vehicleRecorderMediaTokenApiTrigger({
      //     account_id: thisUserOrganizationId ?? "",
      //     device_id:
      //       vehicleId?.length > 0
      //         ? getVehicleFieldValueFromUUID(vehicleId, 'deviceid')
      //         : "",
      //   })
      //     .unwrap()
      //     .then((data) => {
      //       setMediaToken(data);
      //       onMediaTokenRetrieved(data);
      //     })
      //     .catch((err) => {
      //       console.error(err);
      //       toast.error(tMain("general_error")); // use of general_error is not recommended, instead use specific error message
      //     });
      // }
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          `Recorder Status: ${dataVehicleRecorderStatus?.recorder_status}`
        );
      toast.error(t("recorder_status.unknown"));
    }
  }, [
    dataVehicleRecorderStatus,
    dataSingleVehicle,
    dataDeviceListing,
    thisOrgCameraRecorderType,
    thisUserOrganizationId,
    vehicleId,
    modalsState,
    onMediaTokenRetrieved,
  ]);

  if (modalsState.showVehicleDetails === false || !vehicleId) return null;

  const wrapperClass =
    `absolute flex flex-col gap-6 px-4 p-4 mx-auto max-w-[calc(100vw-4rem)] w-[330px] z-map-popup top-2 right-2 bottom-2 max-md:h-[calc(100vh-9rem)] bg-white shadow-2xl rounded-lg overflow-hidden transition` +
    (!!drawerOpen ? "" : " transform translate-x-[calc(100%+0.5em)]");

  if (isFetchingSingleVehicle || isFetchingVehicleEvents)
    return (
      <div className={wrapperClass}>
        <div className="flex justify-between items-center">
          <div
            className={`absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg z-50`}
          >
            <img
              src={svgIcons.LoadingAnimation}
              alt="Loading animation"
              className="w-12 h-12"
            />
          </div>
        </div>
      </div>
    );

  return (
    <div className={wrapperClass}>
      {/* Drawer Handle */}
      <DrawerHandle />

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold leading-6 text-heading-black">
          {t("heading")}
        </h2>
        <button type="button" className="p-1 rounded-full">
          <img
            src={svgIcons.CloseIcon}
            alt="close-icon"
            onClick={() => {
              // setDrawerOpen(false);
              hideModal();
            }}
          />
        </button>
      </div>

      {/* Content */}
      <div className="flex justify-between items-start">
        {/* Vehicle Identification */}
        <div>
          <p className="text-base font-semibold leading-4 text-accent-blue-dark">
            {mapVehicleDisplayTitle(vehicleDataToUse)}
          </p>
          <p className="text-sm font-medium leading-4 mt-2 text-gray-400">
            {/* {mapVehicleDisplayIds(vehicleDataToUse)} */}

            {consistentDisplayAssetItem(
              vehicleDataToUse?.deviceid,
              vehicleDataToUse?.description,
              vehicleDataToUse?.displayname,
              vehicleDataToUse?.drivername,
              showDriver
            )}
          </p>
        </div>

        {/* Live Camera Button */}
        {thisOrgViewVideoAllowed === "1" && (
          // getUserACLAccessLevel(ACL_NAMES.ACL_MAP_VIDEOCAM) >=
          // ROLE_ACCESS_LEVELS.READ &&
          <button
            type="button"
            className={
              `p-3 rounded-full ` +
              `${
                dataSingleVehicle?.is_recording_supported &&
                !isFetchingVehicleRecorderStatus
                  ? "group" // "bg-accent-blue-pale group hover:bg-accent-blue-dark"
                  : "bg-gray-200"
              } ` +
              `${
                isFetchingSingleVehicle ||
                isFetchingVehicleEvents ||
                isFetchingVehicleRecorderStatus
                  ? ""
                  : dataSingleVehicle?.is_recording_supported
                    ? `outline outline-2 focus:outline-accent-blue-dark ${
                        dataVehicleRecorderStatus?.recorder_status === "standby"
                          ? "outline-amber-500/60 bg-amber-500/20 hover:bg-amber-500"
                          : dataVehicleRecorderStatus?.recorder_status ===
                              "offline"
                            ? "outline-field-error-dark/50 bg-field-error-dark/20 hover:bg-field-error-dark"
                            : dataVehicleRecorderStatus?.recorder_status ===
                                "Unknown"
                              ? "outline-gray-700/50 bg-gray-700/20 hover:bg-gray-700"
                              : dataVehicleRecorderStatus?.recorder_status ===
                                  "online"
                                ? "outline-field-success/60 bg-field-success/20 hover:bg-field-success"
                                : ""
                      }`
                    : ""
              } ` +
              `ml-2`
            }
            disabled={
              !dataSingleVehicle?.is_recording_supported ||
              isFetchingVehicleRecorderStatus
            }
            onClick={handleVehicleLiveCameraClick}
          >
            <img
              className={`min-w-5 min-h-5 ${
                !isFetchingSingleVehicle &&
                !isFetchingVehicleEvents &&
                !isFetchingVehicleRecorderStatus &&
                dataSingleVehicle?.is_recording_supported
                  ? "opacity-70"
                  : "opacity-50"
              } grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert`}
              src={
                isFetchingSingleVehicle ||
                isFetchingVehicleEvents ||
                isFetchingVehicleRecorderStatus ||
                isLoadingVehicleRecorderMediaToken
                  ? svgIcons.LoadingAnimation
                  : svgIcons.VideoCameraIcon
              }
              alt="video-camera-icon"
            />
          </button>
        )}
      </div>
      {/* =====
      {!!mediaToken ? (
        <div className="min-h-40">
          <lytx-live-video
            // ref={playerRef}
            auth-token={mediaToken?.media_token}
            imei={dataSingleVehicle?.imeinumber || ''}
            camera-id="1"
            organization-id="5"
          />
        </div>
        // <VehicleLiveVideo
        //   mediaAddress={mediaToken?.address}
        //   mediaToken={mediaToken?.media_token}
        //   vehicle={dataSingleVehicle as TLegacyVehicle}
        //   wrapperClass="w-48 h-24"
        // />
      ) : (
        <p>Media token not available</p>
      )}
      ===== */}
      <div className="flex gap-4">
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("message")}
          type="button"
          variant="primary"
          disabled={
            // !vehicleData?.driverid ? true : // removed as we are not checking for driverid
            isLoadingAssignedDriver ||
            isErrorAssignedDriver ||
            !dataAssignedDriver ||
            !dataAssignedDriver?.contactphone
          }
          onClick={() =>
            dispatch(setModalsData({ ...modalsState, showSendText: true }))
          }
        />
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("send_location")}
          disabled={!eventData?.latitude || !eventData?.longitude}
          type="button"
          variant="primary"
          onClick={() =>
            dispatch(setModalsData({ ...modalsState, showSendLocation: true }))
          }
        />
      </div>
      <div className="space-y-6 flex-grow overflow-y-auto">
        {listData?.map((item, index) => (
          <div key={index}>
            <p className="text-xs font-normal leading-5 Xtracking-tighter text-gray-400">
              {item.name}
            </p>
            <p className="text-sm font-medium leading-5 Xtracking-tighter">
              {item.value}
            </p>
            {item.sub && (
              <p className="text-[10px] font-normal leading-5 Xtracking-tighter text-gray-400">
                {item.sub}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("street_view")}
          type="button"
          variant="primary"
          disabled={!eventData?.latitude || !eventData?.longitude}
          onClick={() => {
            // hideModal(); // hiding modal will trigger deselection of vehicle, instead we just need to hide the modal only in streetview mode
            handleMapTypeChange?.(
              APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETVIEW]
                .type as TMapType,
              !!eventData?.latitude && !!eventData?.longitude
                ? new google.maps.LatLng(
                    eventData?.latitude ?? 0,
                    eventData?.longitude ?? 0
                  )
                : undefined
            );
          }}
        />
        <AdminFormFieldSubmit
          customWrapperClass="flex-1"
          label={t("forecast")}
          type="button"
          variant="primary"
          onClick={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                showWeatherForecast: true,
              })
            );
          }}
          disabled={!eventData?.latitude || !eventData?.longitude}
        />
      </div>
      <AdminFormFieldSubmit
        label={t("vehicle_map_view")}
        type="button"
        variant="primary"
        onClick={() => {
          hideModal();
          navigate(
            `${routeUrls.dashboardChildren.mapsChildren.vehicle}/${vehicleId}`
          );
        }}
        // disabled={
        //   getUserACLAccessLevel(ACL_NAMES.ACL_MAP_ASSET) <
        //   ROLE_ACCESS_LEVELS.READ
        // }
      />
      <ModalWeatherForecast
        latitude={eventData?.latitude ?? 0}
        longitude={eventData?.longitude ?? 0}
        address={eventData?.address ?? ""}
      />
      <ModalSendText assignedDriver={dataAssignedDriver} />
      <ModalSendLocation
        latitude={eventData?.latitude ?? 0}
        longitude={eventData?.longitude ?? 0}
      />
      {modalsState?.showModalMaintenanceFaultCodes && (
        <ModalMaintenanceFaultCodes
          faultCodeData={dataSingleVehicle?.fault_code_info}
          setFaultCodeData={undefined}
        />
      )}
    </div>
  );
};

export default ModalVehicleDetails;
